import type { ProviderHeaders } from "@bastani/pi-ai";
import type { Api, Model } from "@bastani/pi-ai/compat";
import { clampThinkingLevel, getSupportedThinkingLevels, modelsAreEqual } from "@bastani/pi-ai/compat";
import type { ThinkingLevel } from "@earendil-works/pi-agent-core";
import type { AgentSessionInternalSurface as AgentSession } from "./agent-session-methods.ts";
import { type ModelCycleResult, type ModelMutationOptions, THINKING_LEVELS } from "./agent-session-types.js";
import { formatNoApiKeyFoundMessage } from "./auth-guidance.ts";
import { DEFAULT_THINKING_LEVEL } from "./defaults.ts";
import { assertSessionOpen, trackSessionWork } from "./session-lifecycle-work.ts";
import { serializeSessionMutation } from "./session-mutation-queue.ts";
import { settingsWriteOwner } from "./settings-write-ownership.ts";

export async function _getRequiredRequestAuth(
	this: AgentSession,
	model: Model<Api>,
	signal?: AbortSignal,
): Promise<{
	apiKey?: string;
	headers?: ProviderHeaders;
	baseUrl?: string;
	env?: Record<string, string>;
}> {
	let result: Awaited<ReturnType<AgentSession["_modelRuntime"]["getRequestAuth"]>>;
	try {
		result = await this._modelRuntime.getRequestAuth(model, { signal });
	} catch (error) {
		const cause = error instanceof Error ? error.cause : undefined;
		if (cause instanceof Error && cause.message === "authHeader requires a resolved API key") {
			throw new Error(formatNoApiKeyFoundMessage(model.provider));
		}
		throw error;
	}
	if (result && (result.auth.apiKey || result.auth.headers)) {
		// `ProviderHeaders` is `Record<string, string | null>` and a null value
		// suppresses the provider/API default header of the same name. Callers of
		// this function issue real outbound requests (branch summarization and the
		// compaction planner), so both markers and the credential-specific endpoint
		// must reach the request layer intact.
		return {
			apiKey: result.auth.apiKey,
			headers: result.auth.headers,
			...(result.auth.baseUrl === undefined ? {} : { baseUrl: result.auth.baseUrl }),
			env: result.env,
		};
	}
	if (this._modelRuntime.isUsingOAuth(model.provider)) {
		throw new Error(
			`Authentication failed for "${model.provider}". Credentials may have expired or network is unavailable. Run '/login ${model.provider}' to re-authenticate.`,
		);
	}
	throw new Error(formatNoApiKeyFoundMessage(model.provider));
}

/**
 * Install tool hooks once on the Agent instance.
 *
 * The callbacks read `this._extensionRunner` at execution time, so extension reload swaps in the
 * new runner without reinstalling hooks. Extension-specific tool wrappers are still used to adapt
 * registered tool execution to the extension context. Tool call and tool result interception now
 * happens here instead of in wrappers.
 */

export function _emitModelChanged(
	this: AgentSession,
	nextModel: Model<Api>,
	previousModel: Model<Api> | undefined,
	source: "set" | "cycle" | "restore" | "fallback",
): void {
	if (modelsAreEqual(previousModel, nextModel)) return;
	this._emit({
		type: "model_changed",
		model: nextModel,
		previousModel,
		source,
	});
}

export async function _emitModelSelect(
	this: AgentSession,
	nextModel: Model<Api>,
	previousModel: Model<Api> | undefined,
	source: "set" | "cycle" | "restore" | "fallback",
): Promise<void> {
	if (modelsAreEqual(previousModel, nextModel)) return;
	await this._extensionRunner.emit({
		type: "model_select",
		model: nextModel,
		previousModel,
		source,
	});
}

function addPersistedDefaultToNonEmptyScope(session: AgentSession, model: Model<Api>): void {
	if (session._scopedModels.length === 0) return;
	if (session._scopedModels.some((scoped) => modelsAreEqual(scoped.model, model))) return;

	session._scopedModels = [...session._scopedModels, { model }];
	const enabledModels = session.settingsManager.getEnabledModels();
	if (!enabledModels?.length) return;

	const modelReference = `${model.provider}/${model.id}`;
	if (enabledModels.some((pattern) => pattern.toLowerCase() === modelReference.toLowerCase())) return;
	settingsWriteOwner.run(session, () => session.settingsManager.setEnabledModels([...enabledModels, modelReference]));
}

/**
 * Set model directly.
 * Validates that auth is configured, saves to session and settings.
 * @throws Error if no auth is configured for the model
 */

export async function setModel(
	this: AgentSession,
	model: Model<Api>,
	options: ModelMutationOptions = {},
): Promise<void> {
	assertSessionOpen(this);
	return trackSessionWork(this, async () => {
		const previousModel = await serializeSessionMutation(this, async () => {
			if (!this._modelRuntime.hasConfiguredAuth(model.provider)) {
				throw new Error(`No API key for ${model.provider}/${model.id}`);
			}
			const previousModel = this.model;
			const thinkingLevel = this._getThinkingLevelForModelSwitch(model);
			const nextModel = model;
			await this.sessionManager.appendModelChange(nextModel.provider, nextModel.id);
			this._clearFallbackModelScope?.();
			this.agent.state.model = nextModel;
			if (options.persist) {
				settingsWriteOwner.run(this, () =>
					this.settingsManager.setDefaultModelAndProvider(nextModel.provider, nextModel.id),
				);
				addPersistedDefaultToNonEmptyScope(this, nextModel);
			}

			// Re-clamp thinking level for new model's capabilities
			await applyThinkingLevel.call(this, thinkingLevel, options);
			this._refreshBaseSystemPromptFromActiveTools();

			this._emitModelChanged(nextModel, previousModel, "set");
			return previousModel;
		});
		// Extension callbacks may themselves request a model change; never hold the queue across them.
		await this._emitModelSelect(model, previousModel, "set");
	});
}

/**
 * Cycle to next/previous model.
 * Uses scoped models (from --models flag) if available, otherwise all available models.
 * @param direction - "forward" (default) or "backward"
 * @returns The new model info, or undefined if only one model available
 */

export async function cycleModel(
	this: AgentSession,
	direction: "forward" | "backward" = "forward",
	options: ModelMutationOptions = {},
): Promise<ModelCycleResult | undefined> {
	assertSessionOpen(this);
	return trackSessionWork(this, async () => {
		const { previousModel, result } = await serializeSessionMutation(this, async () => {
			const previousModel = this.model;
			const result =
				this._scopedModels.length > 0
					? await this._cycleScopedModel(direction, options)
					: await this._cycleAvailableModel(direction, options);
			return { previousModel, result };
		});
		if (result) await this._emitModelSelect(result.model, previousModel, "cycle");
		return result;
	});
}

export async function _cycleScopedModel(
	this: AgentSession,
	direction: "forward" | "backward",
	options: ModelMutationOptions,
): Promise<ModelCycleResult | undefined> {
	const scopedModels = this._scopedModels.filter((scoped) =>
		this._modelRuntime.hasConfiguredAuth(scoped.model.provider),
	);
	if (scopedModels.length <= 1) return undefined;

	const currentModel = this.model;
	let currentIndex = scopedModels.findIndex((sm) => modelsAreEqual(sm.model, currentModel));

	if (currentIndex === -1) currentIndex = 0;
	const len = scopedModels.length;
	const nextIndex = direction === "forward" ? (currentIndex + 1) % len : (currentIndex - 1 + len) % len;
	const next = scopedModels[nextIndex];
	const thinkingLevel = this._getThinkingLevelForModelSwitch(next.model, next.thinkingLevel);
	const nextModel = next.model;

	// An explicit cycle finishes any active fallback lifecycle before switching.
	await this.sessionManager.appendModelChange(nextModel.provider, nextModel.id);
	this._clearFallbackModelScope?.();
	this.agent.state.model = nextModel;
	if (options.persist) {
		settingsWriteOwner.run(this, () =>
			this.settingsManager.setDefaultModelAndProvider(nextModel.provider, nextModel.id),
		);
		addPersistedDefaultToNonEmptyScope(this, nextModel);
	}

	// Apply thinking level.
	// - Explicit scoped model thinking level overrides current session level
	// - Undefined scoped model thinking level inherits the current session preference
	// setThinkingLevel clamps to model capabilities.
	await applyThinkingLevel.call(this, thinkingLevel, options);
	this._refreshBaseSystemPromptFromActiveTools();

	this._emitModelChanged(nextModel, currentModel, "cycle");

	return { model: nextModel, thinkingLevel: this.thinkingLevel, isScoped: true };
}

export async function _cycleAvailableModel(
	this: AgentSession,
	direction: "forward" | "backward",
	options: ModelMutationOptions,
): Promise<ModelCycleResult | undefined> {
	const availableModels = await this._modelRuntime.getAvailableSnapshot();
	assertSessionOpen(this);
	if (availableModels.length <= 1) return undefined;

	const currentModel = this.model;
	let currentIndex = availableModels.findIndex((m) => modelsAreEqual(m, currentModel));

	if (currentIndex === -1) currentIndex = 0;
	const len = availableModels.length;
	const nextIndex = direction === "forward" ? (currentIndex + 1) % len : (currentIndex - 1 + len) % len;
	const selectedModel = availableModels[nextIndex];

	const thinkingLevel = this._getThinkingLevelForModelSwitch(selectedModel);
	// An explicit cycle finishes any active fallback lifecycle before switching.
	await this.sessionManager.appendModelChange(selectedModel.provider, selectedModel.id);
	this._clearFallbackModelScope?.();
	this.agent.state.model = selectedModel;
	if (options.persist) {
		settingsWriteOwner.run(this, () =>
			this.settingsManager.setDefaultModelAndProvider(selectedModel.provider, selectedModel.id),
		);
		addPersistedDefaultToNonEmptyScope(this, selectedModel);
	}

	// Re-clamp thinking level for new model's capabilities
	await applyThinkingLevel.call(this, thinkingLevel, options);
	this._refreshBaseSystemPromptFromActiveTools();

	this._emitModelChanged(selectedModel, currentModel, "cycle");

	return { model: selectedModel, thinkingLevel: this.thinkingLevel, isScoped: false };
}

// =========================================================================
// Thinking Level Management
// =========================================================================

/**
 * Set thinking level.
 * Clamps to model capabilities based on available thinking levels.
 * Saves to session and settings only if the level actually changes.
 */

function persistThinkingLevel(session: AgentSession, level: ThinkingLevel): void {
	if (session.model) {
		settingsWriteOwner.run(session, () =>
			session.settingsManager.setModelThinkingLevel(session.model!.provider, session.model!.id, level),
		);
	}
	settingsWriteOwner.run(session, () => session.settingsManager.setDefaultThinkingLevel(level));
}

export async function setThinkingLevel(
	this: AgentSession,
	level: ThinkingLevel,
	options: ModelMutationOptions = {},
): Promise<void> {
	return trackSessionWork(this, () =>
		serializeSessionMutation(this, () => applyThinkingLevel.call(this, level, options)),
	);
}

async function applyThinkingLevel(
	this: AgentSession,
	level: ThinkingLevel,
	options: ModelMutationOptions,
): Promise<void> {
	const availableLevels = this.getAvailableThinkingLevels();
	const effectiveLevel = availableLevels.includes(level) ? level : this._clampThinkingLevel(level, availableLevels);

	// Only persist if actually changing
	const previousLevel = this.agent.state.thinkingLevel;
	const isChanging = effectiveLevel !== previousLevel;

	if (isChanging) {
		// A reasoning choice is not a model choice, so it leaves the selected
		// fallback model in place. Keep the active lifecycle open until the turn
		// settles. (A no-op assignment from a registry refresh changes nothing.)
		await this.sessionManager.appendThinkingLevelChange(effectiveLevel);
		this.agent.state.thinkingLevel = effectiveLevel;
		if (options.persist) persistThinkingLevel(this, effectiveLevel);
		if (this._fallbackOriginModel !== undefined) this._fallbackOriginThinkingLevel = effectiveLevel;
		this._refreshBaseSystemPromptFromActiveTools();
		this._emit({ type: "thinking_level_changed", level: effectiveLevel });
		void this._extensionRunner.emit({
			type: "thinking_level_select",
			level: effectiveLevel,
			previousLevel,
		});
	} else if (options.persist) {
		persistThinkingLevel(this, effectiveLevel);
	}
}

/**
 * Cycle to next thinking level.
 * @returns New level, or undefined if model doesn't support thinking
 */

export async function cycleThinkingLevel(
	this: AgentSession,
	options: ModelMutationOptions = {},
): Promise<ThinkingLevel | undefined> {
	return trackSessionWork(this, () =>
		serializeSessionMutation(this, async () => {
			if (!this.supportsThinking()) return undefined;

			const levels = this.getAvailableThinkingLevels();
			const currentIndex = levels.indexOf(this.thinkingLevel);
			const nextIndex = (currentIndex + 1) % levels.length;
			const nextLevel = levels[nextIndex];

			await applyThinkingLevel.call(this, nextLevel, options);
			return nextLevel;
		}),
	);
}

/**
 * Get available thinking levels for current model.
 * The provider will clamp to what the specific model supports internally.
 */

export function getAvailableThinkingLevels(this: AgentSession): ThinkingLevel[] {
	if (!this.model) return THINKING_LEVELS;
	return getSupportedThinkingLevels(this.model) as ThinkingLevel[];
}

/**
 * Check if current model supports thinking/reasoning.
 */

export function supportsThinking(this: AgentSession): boolean {
	return !!this.model?.reasoning;
}

export function _getThinkingLevelForModelSwitch(
	this: AgentSession,
	targetModel?: Model<Api>,
	explicitLevel?: ThinkingLevel,
): ThinkingLevel {
	if (explicitLevel !== undefined) {
		return explicitLevel;
	}
	if (targetModel) {
		const perModel = this.settingsManager.getModelThinkingLevel(targetModel.provider, targetModel.id);
		if (perModel !== undefined) return perModel;
	}
	if (!this.supportsThinking()) {
		return this.settingsManager.getDefaultThinkingLevel() ?? DEFAULT_THINKING_LEVEL;
	}
	return this.thinkingLevel;
}

export function _clampThinkingLevel(
	this: AgentSession,
	level: ThinkingLevel,
	_availableLevels: ThinkingLevel[],
): ThinkingLevel {
	return this.model ? (clampThinkingLevel(this.model, level) as ThinkingLevel) : "off";
}

// =========================================================================
// Queue Mode Management
// =========================================================================

/**
 * Set steering message mode.
 * Saves to settings.
 */

export const agentSessionModelsMethods = {
	_getRequiredRequestAuth,
	_emitModelChanged,
	_emitModelSelect,
	setModel,
	cycleModel,
	_cycleScopedModel,
	_cycleAvailableModel,
	setThinkingLevel,
	cycleThinkingLevel,
	getAvailableThinkingLevels,
	supportsThinking,
	_getThinkingLevelForModelSwitch,
	_clampThinkingLevel,
};
